import { Head, Link, router, usePage } from "@inertiajs/react"
import { Trash2, User as UserIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import type { PageProps } from "@/types/inertia"

type UserRow = {
  id: number
  email: string
  admin: boolean
  created_at: string
}

export default function AdminUsersIndex({ users }: { users: UserRow[] }) {
  const { props } = usePage<PageProps>()
  const currentUserId = props.current_user?.id

  const deleteUser = (user: UserRow) => {
    if (window.confirm(`Delete ${user.email}? This permanently removes the account.`)) {
      router.delete(`/admin/users/${user.id}`)
    }
  }

  return (
    <>
      <Head title="Users">
        <meta name="description" content="Manage users in the admin area." />
        <meta property="og:title" content="Users" />
        <meta property="og:description" content="Manage users in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Users</h1>
              <p className="mt-1">
                {users.length} {users.length === 1 ? "user" : "users"} in this app.
              </p>
            </div>
          </div>
        </div>

        <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
          {users.map((user) => (
            <li key={user.id} className="flex items-center gap-3 px-4 py-3 hover:bg-surface">
              <UserIcon className="h-4 w-4 shrink-0 text-ink-muted" />
              <Link href={`/admin/users/${user.id}`} className="min-w-0 flex-1 no-underline">
                <div className="truncate text-sm font-medium text-ink-display">{user.email}</div>
                <div className="truncate text-xs text-ink-muted">Joined {formatDate(user.created_at)}</div>
              </Link>
              {user.admin && <Badge tone="accent">Admin</Badge>}
              {user.id === currentUserId ? (
                <span className="text-xs text-ink-muted">You</span>
              ) : (
                <button
                  type="button"
                  onClick={() => deleteUser(user)}
                  aria-label={`Delete ${user.email}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-danger-faded hover:text-danger-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
